import { Fragment, useEffect } from "react";
import { F, localDate, type Row } from "./types";
import { dayLabel } from "./rows";
import {
  foldedBodies, matchMonths, snippetAround, splitHighlight,
  RESULT_CAP, type Match, type Month,
} from "./search";

/** How much of a long message a result card shows, either side of the match.
 *  Bodies average 51 characters and top out around 213, so 60 covers almost
 *  everything whole and windows only the ones that would not fit anyway. */
const SNIPPET_RADIUS = 60;

/** The sheet that becomes a bar.
 *
 *  Expanded, it is how you find something: months to jump to above, matching
 *  messages below. Collapsed, it is how you move through what you found without
 *  losing sight of the conversation -- which is the whole reason search does not
 *  filter the timeline.
 *
 *  Search itself is 0.5 ms over 19,000 rows, so there is no debounce and no
 *  endpoint. The one cost is folding the archive, and that is warmed here, on
 *  open, so the 18 ms lands under the sheet's own animation rather than under
 *  the first character typed.
 */
export function SearchSheet({
  rows, query, onQuery, matches, months, active, expanded,
  onExpand, onPick, onStep, onMonth, onClose,
}: {
  rows: Row[];
  query: string;
  onQuery: (q: string) => void;
  matches: Match[];
  months: Month[];
  active: number;
  expanded: boolean;
  onExpand: () => void;
  onPick: (matchIndex: number) => void;
  onStep: (delta: number) => void;
  onMonth: (month: Month) => void;
  onClose: () => void;
}) {
  useEffect(() => { foldedBodies(rows); }, [rows]);

  if (!expanded) {
    return (
      <div className="bar" data-testid="bar">
        <button type="button" className="q" onClick={onExpand}>
          &#128269; {query}
        </button>
        <span className="count">{active + 1} of {matches.length}</span>
        <button type="button" aria-label="previous match"
                disabled={matches.length === 0} onClick={() => onStep(-1)}>&#8593;</button>
        <button type="button" aria-label="next match"
                disabled={matches.length === 0} onClick={() => onStep(1)}>&#8595;</button>
        <button type="button" aria-label="close search" onClick={onClose}>&#10005;</button>
      </div>
    );
  }

  const monthHits = matchMonths(months, query);
  const shown = matches.slice(0, RESULT_CAP);
  const hidden = matches.length - shown.length;

  return (
    <div className="sheet" data-testid="sheet">
      <div className="sheettop">
        <input
          type="search"
          className="qinput"
          value={query}
          autoFocus
          placeholder="Search messages"
          aria-label="search messages"
          onChange={(e) => onQuery(e.target.value)}
        />
        <button type="button" aria-label="close search" onClick={onClose}>&#10005;</button>
      </div>

      {monthHits.length > 0 && (
        <div className="months" data-testid="months">
          <h2>Jump to</h2>
          {monthHits.map((m) => (
            <button type="button" key={m.ym} onClick={() => onMonth(m)}>
              <span className="mlabel">{m.label}</span>
              <span className="mcount">{m.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="results" data-testid="results">
        {query !== "" && matches.length === 0 && (
          <p className="none">No messages match that.</p>
        )}
        {shown.map((m, i) => {
          const row = rows[m.index];
          const snip = snippetAround(row[F.Body]!, m.ranges, SNIPPET_RADIUS);
          return (
            <button type="button" key={m.index} className="result" onClick={() => onPick(i)}>
              <span className="rhead">
                <span className="rday">{dayLabel(localDate(row))}</span>
                <span className="rwho">{row[F.Sender] || "no sender"}</span>
              </span>
              <span className="rsnip">
                {snip.clipped && "…"}
                {splitHighlight(snip.text, snip.ranges).map((seg, k) =>
                  seg.hit ? <mark key={k}>{seg.text}</mark>
                          : <Fragment key={k}>{seg.text}</Fragment>)}
              </span>
            </button>
          );
        })}
        {hidden > 0 && (
          // Never a silent cap. Finding all of them is 0.5 ms; DRAWING them is
          // the expensive part, and the arrows still reach every one.
          <p className="more">&#8230;{hidden} more, reachable with the arrows</p>
        )}
      </div>
    </div>
  );
}
